'use client';

import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

interface ITurnstileApi {
  execute: (widgetId: string) => void;
  render: (
    element: HTMLElement,
    options: {
      callback: (token: string) => void;
      'error-callback': () => void;
      appearance: 'interaction-only';
      execution: 'execute';
      sitekey: string;
    },
  ) => string;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: ITurnstileApi;
  }
}

interface IEnglishIqTurnstileProps {
  executeSignal: number;
  onError: () => void;
  onToken: (token?: string) => void;
  siteKey: string;
}

const TURNSTILE_READINESS_TIMEOUT_MS = 10_000;

function runTurnstile(widgetId: string | null, isWidgetReady: boolean): boolean {
  if (!isWidgetReady || !widgetId || !window.turnstile) {
    return false;
  }
  window.turnstile.reset(widgetId);
  window.turnstile.execute(widgetId);
  return true;
}

export function EnglishIqTurnstile({
  executeSignal,
  onError,
  onToken,
  siteKey,
}: IEnglishIqTurnstileProps): React.JSX.Element | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const failedExecutionRef = useRef(0);
  const widgetIdRef = useRef<string | null>(null);
  const executedSignalRef = useRef(0);
  const onErrorRef = useRef(onError);
  const onTokenRef = useRef(onToken);
  const [isScriptReady, setIsScriptReady] = useState(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  onErrorRef.current = onError;
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey || !isScriptReady || !containerRef.current || !window.turnstile) {
      return;
    }
    if (!widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        callback: (token) => onTokenRef.current(token),
        'error-callback': () => onErrorRef.current(),
        appearance: 'interaction-only',
        execution: 'execute',
        sitekey: siteKey,
      });
      setIsWidgetReady(true);
    }
  }, [isScriptReady, siteKey]);

  useEffect(() => {
    if (executeSignal === 0 || executedSignalRef.current === executeSignal) {
      return;
    }
    if (!siteKey) {
      if (failedExecutionRef.current !== executeSignal) {
        failedExecutionRef.current = executeSignal;
        onErrorRef.current();
      }
      return;
    }
    if (runTurnstile(widgetIdRef.current, isWidgetReady)) {
      executedSignalRef.current = executeSignal;
    }
  }, [executeSignal, isWidgetReady, siteKey]);

  useEffect(() => {
    if (
      executeSignal === 0 ||
      !siteKey ||
      isWidgetReady ||
      failedExecutionRef.current === executeSignal
    ) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      failedExecutionRef.current = executeSignal;
      onErrorRef.current();
    }, TURNSTILE_READINESS_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [executeSignal, isWidgetReady, siteKey]);

  if (!siteKey) {
    return null;
  }
  return (
    <>
      <Script
        id="english-iq-turnstile"
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        onError={onError}
        onLoad={() => setIsScriptReady(true)}
      />
      <div ref={containerRef} />
    </>
  );
}
